import {
  CalendarIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  FlagIcon,
  MapPinIcon,
  PhotoIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  Bars3Icon,
  EyeSlashIcon,
  HashtagIcon,
} from '@heroicons/react/24/solid';
import { FormFieldType, HtmlStepperFieldType } from '@superfunnel/types';
import { ReactElement } from 'react';

/** Combined field type for both FormField and HtmlStepperField */
export type AnyFieldType = FormFieldType | HtmlStepperFieldType;

export const getFormFieldTypeLabel = (type: AnyFieldType): string => {
  switch (type) {
    case 'email':
      return 'Email';

    case 'tel':
      return 'Phone';

    case 'text':
      return 'Text';

    case 'textArea':
    case 'textarea': // HtmlStepperFieldType uses lowercase
      return 'Text area';

    case 'businessEmail':
      return 'Business email';

    case 'country':
      return 'Country';

    case 'state':
      return 'State';

    case 'number':
      return 'Number';

    case 'range':
      return 'Range';

    case 'select':
      return 'Select';

    case 'hidden':
      return 'Hidden';

    case 'pictureChoice':
      return 'Picture choice';

    case 'consent':
      return 'Consent';

    case 'multiChoice':
      return 'Multi choice';

    case 'date':
      return 'Date';

    case 'checkbox':
      return 'Checkbox';

    case 'phone':
      return 'Phone Input';

    case 'address':
      return 'Address';

    default:
      return type;
  }
};

export const getFormFieldTypeIcon = (
  type: AnyFieldType,
): ReactElement | undefined => {
  switch (type) {
    case 'email':
      return <EnvelopeIcon className="h-4 w-4" />;

    case 'tel':
    case 'phone':
      return <DevicePhoneMobileIcon className="h-4 w-4" />;

    case 'text':
      return <DocumentTextIcon className="h-4 w-4" />;

    case 'textArea':
    case 'textarea': // HtmlStepperFieldType uses lowercase
      return <DocumentTextIcon className="h-4 w-4" />;

    case 'businessEmail':
      return <EnvelopeIcon className="h-4 w-4" />;

    case 'country':
      return <FlagIcon className="h-4 w-4" />;

    case 'state':
      return <FlagIcon className="h-4 w-4" />;

    case 'number':
    case 'range':
      return <HashtagIcon className="h-4 w-4" />;

    case 'select':
      return <Bars3Icon className="h-4 w-4" />;

    case 'hidden':
      return <EyeSlashIcon className="h-4 w-4" />;

    case 'pictureChoice':
      return <PhotoIcon className="h-4 w-4" />;

    case 'consent':
    case 'checkbox':
      return <ShieldCheckIcon className="h-4 w-4" />;

    case 'multiChoice':
      return <Bars3Icon className="h-4 w-4" />;

    case 'date':
      return <CalendarIcon className="h-4 w-4" />;

    case 'address':
      return <MapPinIcon className="h-4 w-4" />;

    default:
      return undefined;
  }
};

// Operator field types
// Note: We use (FormFieldType | string)[] to also support HtmlStepperFieldType
// which uses 'textarea' (lowercase) instead of 'textArea'
export const textOperatorFields: (FormFieldType | string)[] = [
  'text',
  'tel',
  'email',
  'textArea',
  'textarea', // HtmlStepperFieldType uses lowercase
  'businessEmail',
  'country',
  'state',
];

export const numberOperatorFields: (FormFieldType | string)[] = [
  'number',
  'range',
];

export const selectOperatorFields: (FormFieldType | string)[] = [
  'select',
  'multiChoice',
  'pictureChoice',
];

// Operators for conditions
export const textOperators: { value: string; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'startsWith', label: 'Starts With' },
  { value: 'endsWith', label: 'Ends With' },
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Not Contains' },
  { value: 'isEmpty', label: 'Is Empty' },
];

export const numberOperators: { value: string; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'greaterThan', label: 'Greater Than' },
  { value: 'lessThan', label: 'Less Than' },
  { value: 'greaterThanOrEqualTo', label: 'Greater Than Or Equal To' },
  { value: 'lessThanOrEqualTo', label: 'Less Than Or Equal To' },
];

export const selectOperators: { value: string; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'isAny', label: 'Is Any' },
  { value: 'hasAll', label: 'Has All Of' },
  { value: 'hasNone', label: 'Has None Of' },
  { value: 'isEmpty', label: 'Is Empty' },
];

// Hidden field operators - combines text, number, and date operators
// since hidden fields can contain any type of data
export const hiddenOperators: {
  value: string;
  label: string;
  group: 'text' | 'number' | 'date';
}[] = [
  // Text operators
  { value: 'equals', label: 'Equals', group: 'text' },
  { value: 'notEquals', label: 'Not Equals', group: 'text' },
  { value: 'startsWith', label: 'Starts With', group: 'text' },
  { value: 'endsWith', label: 'Ends With', group: 'text' },
  { value: 'contains', label: 'Contains', group: 'text' },
  { value: 'notContains', label: 'Not Contains', group: 'text' },
  { value: 'isEmpty', label: 'Is Empty', group: 'text' },
  // Number operators
  { value: 'greaterThan', label: 'Greater Than', group: 'number' },
  { value: 'lessThan', label: 'Less Than', group: 'number' },
  {
    value: 'greaterThanOrEqualTo',
    label: 'Greater Than Or Equal To',
    group: 'number',
  },
  {
    value: 'lessThanOrEqualTo',
    label: 'Less Than Or Equal To',
    group: 'number',
  },
  // Date operators
  { value: 'dateBefore', label: 'Before', group: 'date' },
  { value: 'dateAfter', label: 'After', group: 'date' },
  { value: 'dateEquals', label: 'On Date', group: 'date' },
];

/** Get the input type for a hidden field operator */
export const getHiddenOperatorInputType = (
  operator: string,
): 'text' | 'number' | 'date' | 'none' => {
  if (operator === 'isEmpty') return 'none';
  const op = hiddenOperators.find((o) => o.value === operator);
  return op?.group ?? 'text';
};
